import { Configuration, SDKApi } from '@dynamic-labs/sdk-api-core';

import { version } from '../../../package.json';
import { getCore } from '../../client/core/getCore';
import type { DynamicClient } from '../../client/types';
import { getPlatform } from '../../utils/getPlatform/getPlatform';
import { randomString } from '../../utils/randomString';
import { getElevatedAccessToken } from '../auth/getElevatedAccessToken';
import { isCookieEnabled } from '../projectSettings/isCookieEnabled';
import { getSessionKeys } from '../sessionKeys/getSessionKeys';
import type { CreateApiClientOptions } from './apiClient.types';
import {
  CLIENT_SDK_NAME,
  DYNAMIC_API_VERSION_HEADER,
  DYNAMIC_PLATFORM_HEADER,
  DYNAMIC_REQUEST_ID_HEADER,
  DYNAMIC_SDK_API_VERSION,
  DYNAMIC_SDK_SESSION_ID_HEADER,
  DYNAMIC_SDK_VERSION_HEADER,
  ELEVATED_ACCESS_TOKEN_HEADER,
  MFA_TOKEN_HEADER,
  SESSION_PUBLIC_KEY_HEADER,
} from './constants';
import { clientErrorMapper } from './utils/clientErrorMapper';
import { createConvertToApiErrorMiddleware } from './utils/convertToApiErrorMiddleware/convertToApiErrorMiddleware';
import { createDeviceSignatureHeadersMiddleware } from './utils/deviceSignatureHeadersMiddleware';
import { createUnauthorizedMiddleware } from './utils/unauthorizedMiddleware/createUnauthorizedMiddleware';

type ISettings = {
  basePath: string;
  credentials?: 'include';
  headers: {
    Authorization?: string;
    'Content-Type': string;
    [DYNAMIC_API_VERSION_HEADER]: string;
    [DYNAMIC_PLATFORM_HEADER]: string;
    [DYNAMIC_REQUEST_ID_HEADER]: string;
    [DYNAMIC_SDK_SESSION_ID_HEADER]: string;
    [DYNAMIC_SDK_VERSION_HEADER]: string;
    [ELEVATED_ACCESS_TOKEN_HEADER]?: string;
    [MFA_TOKEN_HEADER]?: string;
    [SESSION_PUBLIC_KEY_HEADER]?: string;
  };
};

/**
 * Returns a new instance of the SDK API client.
 *
 * This is not meant for storing, as it is very light we can create it whenever needed.
 * @notInstrumented
 */
export const createApiClient = (
  options: CreateApiClientOptions = {},
  client: DynamicClient
) => {
  const core = getCore(client);
  const coreState = core.state.get();

  const settings: ISettings = {
    basePath: core.apiBaseUrl,
    headers: {
      'Content-Type': 'application/json',
      [DYNAMIC_API_VERSION_HEADER]: `API/${DYNAMIC_SDK_API_VERSION}`,
      [DYNAMIC_PLATFORM_HEADER]: getPlatform(),
      [DYNAMIC_REQUEST_ID_HEADER]: randomString({ length: 50 }),
      [DYNAMIC_SDK_SESSION_ID_HEADER]: core.sdkSessionId,
      [DYNAMIC_SDK_VERSION_HEADER]: `${CLIENT_SDK_NAME}/${version}`,
      ...core.getApiHeaders(),
      ...options.headers,
    },
  };

  if (core.metadata?.universalLink) {
    (settings.headers as Record<string, string>)['Origin'] =
      core.metadata.universalLink;
  }

  if (client.token) {
    settings.headers.Authorization = `Bearer ${client.token}`;
  }

  if (client.projectSettings && isCookieEnabled(client)) {
    settings.credentials = 'include';
  }

  if (options.includeMfaToken && coreState.mfaToken) {
    settings.headers[MFA_TOKEN_HEADER] = coreState.mfaToken;
  }

  if (options.elevatedAccessTokenScope) {
    const elevatedToken = getElevatedAccessToken(
      { scope: options.elevatedAccessTokenScope },
      client
    );
    if (elevatedToken) {
      settings.headers[ELEVATED_ACCESS_TOKEN_HEADER] = elevatedToken;
    }
  }

  const sessionPublicKey = getSessionKeys(client)?.publicKey;

  // This check is required to avoid overriding the session public key header
  // that is set by any of our other SDKs using the Dynamic Client SDK.
  const isSessionPublicKeyHeaderPresent =
    settings.headers[SESSION_PUBLIC_KEY_HEADER] !== undefined;

  if (sessionPublicKey && !isSessionPublicKeyHeaderPresent) {
    settings.headers[SESSION_PUBLIC_KEY_HEADER] = sessionPublicKey;
  }

  return new SDKApi(
    new Configuration({
      ...settings,
      fetchApi: core.fetch,
      middleware: [
        createDeviceSignatureHeadersMiddleware(client),
        createConvertToApiErrorMiddleware({
          errorMappers: [...(options.errorMappers || []), clientErrorMapper],
        }),
        createUnauthorizedMiddleware(),
      ],
    })
  );
};
